import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { questions } from '../services/questionService';
import { UserAnswers } from '../types';
import { LeadCaptureForm } from './LeadCaptureForm';

export function Questionnaire() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<UserAnswers>({});
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const navigate = useNavigate();

  const currentQuestion = questions[currentStep];

  const handleAnswer = (value: string | string[]) => {
    const currentQuestion = questions[currentStep];
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));

    if (!currentQuestion.multiSelect) {
      if (currentStep < questions.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowLeadCapture(true);
      }
    }
  };

  const handleMultiSelect = (value: string) => {
    const currentQuestion = questions[currentStep];
    const currentValues = (answers[currentQuestion.id] as string[]) || [];
    let newValues: string[];

    if (value === 'none') {
      newValues = ['none'];
    } else {
      if (currentValues.includes(value)) {
        newValues = currentValues.filter(v => v !== value && v !== 'none');
      } else {
        newValues = [...currentValues.filter(v => v !== 'none'), value];
      }
    }

    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: newValues
    }));
  };

  const handleContinue = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowLeadCapture(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (showLeadCapture) {
    return (
      <div className="flex justify-center items-center min-h-[80vh]">
        <LeadCaptureForm 
          onSubmit={(data) => {
            navigate('/results', { 
              state: { 
                answers,
                leadData: data 
              } 
            });
          }}
          onSkip={() => {
            navigate('/results', { state: { answers } });
          }}
        />
      </div>
    );
  }

  const isMultiSelect = currentQuestion?.multiSelect;
  const selectedValues = (answers[currentQuestion?.id] as string[]) || [];
  const isStateQuestion = currentQuestion?.id === 'state';

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">
            {currentQuestion?.title}
          </h2>
          <span className="text-sm text-gray-500">
            Step {currentStep + 1} of {questions.length}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
            style={{
              width: `${((currentStep + 1) / questions.length) * 100}%`,
            }}
          ></div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h3 className="text-xl text-gray-800 mb-6">{currentQuestion?.question}</h3>
        
        {isStateQuestion ? (
          <div className="w-full">
            <select
              value={answers[currentQuestion.id] as string || ''}
              onChange={(e) => handleAnswer(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select a state</option>
              {currentQuestion.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="space-y-4">
            {currentQuestion?.options.map((option) => (
              <button
                key={option.value}
                onClick={() => isMultiSelect ? handleMultiSelect(option.value) : handleAnswer(option.value)}
                className={`w-full text-left px-6 py-4 border rounded-lg transition-colors duration-200 ${
                  isMultiSelect 
                    ? selectedValues.includes(option.value)
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-500 hover:bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-500 hover:bg-indigo-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-900">{option.label}</span>
                  {isMultiSelect && (
                    <div className={`w-5 h-5 border rounded ${
                      selectedValues.includes(option.value)
                        ? 'bg-indigo-600 border-indigo-600'
                        : 'border-gray-300'
                    } flex items-center justify-center`}>
                      {selectedValues.includes(option.value) && (
                        <span className="text-white text-sm">✓</span>
                      )}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center">
        {currentStep > 0 ? (
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
        ) : (
          <div></div>
        )}
        
        {isMultiSelect && selectedValues.length > 0 && (
          <button
            onClick={handleContinue}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
          >
            Continue
          </button>
        )}
        
        {!isMultiSelect && !isStateQuestion && (
          <div className="text-sm text-gray-500">
            {currentStep + 1} of {questions.length}
          </div>
        )}
      </div>
    </div>
  );
}